/**
 * @element qds-feedback-button
 * 
 * A feedback button component for collecting user comments and suggestions.
 * 
 * @attr {string} href - URL that the feedback button links to
 * @attr {string} target - Where to open the link (_blank, _self, etc.)
 * 
 * @csspart button - The button element
 * @csspart icon - The icon element
 * 
 * @fires click - Fired when the button is clicked
 */

import { adoptPrimeIconsStyles, loadBaseCSS, getBaseButtonStyles } from '../shared/button-utils.js';

// Custom event interface
interface FeedbackButtonClickEventDetail {
  href: string;
}

// Load the base CSS
loadBaseCSS();

export class QdsFeedbackButton extends HTMLElement {
  // Private properties
  private _href: string;
  private _target: string;
  private _boundClickHandler?: (event: MouseEvent) => void;

  static get observedAttributes(): string[] {
    return ['href', 'target'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Initialize properties with default values
    this._href = '#';
    this._target = '_blank';
    
    this.render();
  }

  // Lifecycle callbacks
  connectedCallback(): void {
    if (!this.isConnected) return;
    
    // Store bound handler for proper cleanup
    this._boundClickHandler = this._handleClick.bind(this);
    
    // Add click event listener
    const button = this.shadowRoot?.querySelector('button');
    if (button && this._boundClickHandler) {
      button.addEventListener('click', this._boundClickHandler);
    }
  }

  disconnectedCallback(): void {
    // Clean up event listeners
    const button = this.shadowRoot?.querySelector('button');
    if (button && this._boundClickHandler) {
      button.removeEventListener('click', this._boundClickHandler);
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'href':
        this._href = newValue || '#';
        break;
      case 'target':
        this._target = newValue || '_blank';
        break;
    }
    
    this.render();
  }

  // Getters and setters
  get href(): string {
    return this._href;
  }
  
  set href(value: string) {
    this.setAttribute('href', value);
  }
  
  get target(): string {
    return this._target;
  }
  
  set target(value: string) {
    this.setAttribute('target', value);
  }

  // Event handlers
  private _handleClick(event: MouseEvent): void {
    if (this._href && this._href !== '#') {
      window.open(this._href, this._target);
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent<FeedbackButtonClickEventDetail>('click', {
      bubbles: true,
      composed: true,
      detail: { href: this._href }
    }));
  }

  // Render the component
  private render(): void {
    if (!this.shadowRoot) return;

    const styles = `
      ${getBaseButtonStyles()}

      .menu-label {
        display: none;
        margin-left: 8px;
        font-size: 14px;
        color: var(--theme-text-secondary, var(--theme-text-primary, inherit));
      }

      /* Show label in mobile menu */
      @media (max-width: 480px) {
        .mobile-menu .menu-label {
          display: inline-block;
        }
      }
    `;

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <button part="button" aria-label="Feedback">
        <i class="pi pi-comments icon" part="icon"></i>
        <span class="menu-label">Feedback</span>
      </button>
    `;
    void adoptPrimeIconsStyles(this.shadowRoot);
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-feedback-button')) {
  customElements.define('qds-feedback-button', QdsFeedbackButton);
}
